import { IonText } from '@ionic/react'
import { checkmark } from 'ionicons/icons'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    UserMetaDataDocument,
    useUpdateUserMetaDataMutation,
    useUserMetaDataQuery,
} from '../../../lib/apollo/types'
import type { UserMetaData ,
    User } from '../../../lib/apollo/types'
import { useUser } from '../../../providers/Auth/hooks'
import useToast from '../../../providers/Toast/hooks'
import Button from '../../Button'
import Buttons from '../../Buttons'
import Modal from '../../Modal'
import StatusChangeForm from '../../StatusChangeForm'

import '../style.scss'

interface UserStatusProps {
    user: User
    /**
     * Optional, weil es das wirklich sein kann: Ein Konto ohne gespeicherte
     * Profildaten hat keine Metadaten. Vorher stand hier ein Pflichtfeld, und
     * der Aufrufer hat die Luecke mit `as UserMetaData` ueberdeckt — der
     * Compiler konnte den Absturz beim Speichern deshalb nicht sehen.
     *
     * Nur fuer fremde Profile die Quelle der Wahrheit: fuer das eigene wird
     * unten nachgeladen, siehe `useUserMetaDataQuery`.
     */
    userMeta?: UserMetaData | null
}

const UserStatus: React.FC<UserStatusProps> = ({ user, userMeta }) => {
    const { t } = useTranslation()
    const [showToast] = useToast()
    const me = useUser()

    const editable = me?.user.id === user.id

    /**
     * `saveUserMetaData` ist ein PUT und ersetzt den Datensatz vollstaendig —
     * was hier nicht mitgeschickt wird, ist danach weg. Der Aufrufer kann die
     * Metadaten aber nicht zuverlaessig liefern: auf dem eigenen Profil-Tab
     * kommt der User aus `whoami`, und dessen Fragment enthaelt kein
     * `userMetaData`. Gespeichert wurden dann nur `username` + `status`, womit
     * u.a. `behavior.termsAcceptedAt` verschwand und der AuthProvider einen
     * wieder durch das Onboarding (AGB) geschickt hat.
     *
     * Deshalb laedt die Komponente, die schreibt, den aktuellen Stand selbst.
     * Fremde Profile brauchen das nicht — dort wird nur angezeigt.
     */
    const {
        data: ownMeta,
        loading: ownMetaLoading,
        error: ownMetaError,
    } = useUserMetaDataQuery({
        variables: {
            userId: user.id,
        },
        skip: !editable,
        fetchPolicy: 'cache-and-network',
    })

    const metaData = (editable ? ownMeta?.userMetaData?.user?.data : undefined) ?? userMeta

    const [updateUserMetaData, { loading }] = useUpdateUserMetaDataMutation({
        refetchQueries: [{
            query: UserMetaDataDocument,
            variables: {
                userId: user.id,
            },
        }],
    })

    const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
    // Nur der Bearbeitungsstand des Modals; angezeigt wird sonst der Server-Wert.
    const [status, setStatus] = useState<string | number | null | undefined>()

    const userStates = useMemo(() => (new Array(7))
        .fill(undefined)
        .map((_, i) => t(`status.userStatus${i}`)), [t])

    const talentStates = useMemo(() => (new Array(7))
        .fill(undefined)
        .map((_, i) => t(`status.talentStatus${i}`)), [t])

    const onChange = (val?: string | number | null) => { setStatus(val) }

    const openModal = useCallback(() => {
        setStatus(metaData?.status ?? '')
        setShowStatusChangeModal(() => true)
    }, [metaData?.status])

    /**
     * Schreibt den vollstaendigen Datensatz zurueck — gleiche Reihenfolge wie in
     * `ProfileEdit`: erst alles Bestehende, dann die geaenderten Felder.
     * `username` ist im Input Pflicht und steht sonst nur in den Metadaten;
     * ohne ihn findet auch der Cache den Datensatz nicht wieder, er normalisiert
     * `UserMetaData` darauf.
     */
    const onSave = async () => {
        if (editable && (ownMetaLoading || ownMetaError)) {
            showToast(
                t('status.notLoaded', 'Dein Profil ist noch nicht geladen. Bitte versuche es gleich noch einmal.'),
                'danger',
            )
            return
        }

        const userMetaData = {
            ...metaData,
            behavior: {
                ...metaData?.behavior,
                __typename: undefined,
            },
            __typename: undefined,
        }

        await updateUserMetaData({
            variables: {
                input: {
                    ...userMetaData,
                    username: metaData?.username ?? me?.user.name ?? '',
                    status: status as string,
                },
            },
        })

        showToast(t('status.changed'), 'success')
        setShowStatusChangeModal(() => false)
    }

    return (
        <>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
                role={!editable ? undefined : 'button'}
                tabIndex={!editable ? undefined : -1}
                onKeyDown={!editable ? undefined : (e) => {
                    if (e.key === 'Enter') {
                        openModal()
                    }
                }}
                onClick={!editable ? undefined : openModal}
                className='status'
            >
                <IonText
                    color='medium'
                >
                    <em>{ (status ?? metaData?.status) || t('user.status.placeHolder') }</em>
                </IonText>

            </div>
            <Modal
                modalTitle={t('status.userStatusChangeModalTitle')}
                isOpen={showStatusChangeModal}
                onClose={() => { setShowStatusChangeModal(() => false) }}
            >
                <StatusChangeForm
                    predefinedStates={[...userStates, ...talentStates]}
                    onChange={onChange}
                    status={status as string ?? ''}
                />
                <Buttons>
                    <Button
                        loading={loading}
                        color='secondary'
                        icon={checkmark}
                        onClick={onSave}
                    >
                        {t('buttons.save')}
                    </Button>
                </Buttons>
            </Modal>
        </>
    )
}

export default UserStatus
